// components/CollapsibleNotesGroup.jsx
import React, { useMemo, useState } from 'react';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Collapse,
	FormLabel,
	Link,
	Stack,
	Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { getUrlFromPipeline } from '@/utils/constants';
import { paths } from '@/app/routes';
import { TransitionGroup } from 'react-transition-group';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useListNotesQuery, useCountNotesQuery } from '@/features/engagements/engagementsApi';
import { NoteAccordion } from '@/components/cards/NoteAccordion';

const RenderNote = ({ note, expanded }) => {
	return (
		<Collapse in={expanded}>
			<NoteAccordion note={note} />
		</Collapse>
	);
};
RenderNote.displayName = 'RenderNote';

export const CollapsibleNotesGroup = () => {
	const { dealRow, clientId, dealId } = useObjectContext();

	const location = useLocation();

	const pipeline = dealRow?.pipeline;

	const [expanded, setExpanded] = useState(true);

	const {
		currentData: notesData,
		isError,
		isSuccess,
		isLoading,
	} = useListNotesQuery(
		{ filters: dealId ? { dealId } : { clientId }, limit: 3 },
		{ skip: !dealId && !clientId },
	);

	const { data: countData } = useCountNotesQuery(
		{ filters: dealId ? { dealId } : { clientId } },
		{ skip: !dealId && !clientId },
	);

	const notes = useMemo(() => {
		if (!isSuccess || !notesData?.data) return [];
		return notesData.data;
	}, [notesData, isSuccess]);

	const totalNotes = countData?.count ?? 0;

	const notesUrl = useMemo(() => {
		if (dealId && pipeline) {
			return `${getUrlFromPipeline(pipeline)}/${dealId}/notes`;
		}
		if (clientId) {
			const basePath = location.pathname.startsWith(paths.prospects)
				? paths.prospects
				: paths.clients;
			return `${basePath}/${clientId}/notes`;
		}
		return '/';
	}, [pipeline, dealId, clientId, location.pathname]);

	if (!dealId && !clientId) return null;

	return (
		<Accordion
			elevation={0}
			disableGutters
			square
			expanded={expanded}
			sx={{
				backgroundColor: 'transparent',
				width: '100%',
				'&:not(:last-child)': {
					borderBottom: 0,
				},
				'&::before': {
					display: 'none',
				},
			}}
			onChange={() => setExpanded((prev) => !prev)}
		>
			<AccordionSummary
				expandIcon={
					totalNotes > 0 ? (
						<ExpandMore sx={(theme) => ({ color: theme.palette.primary.main })} />
					) : (
						<></>
					)
				}
				sx={{
					padding: 0,
					'& .MuiAccordionSummary-content': {
						marginRight: 2,
					},
				}}
			>
				<Stack
					direction='row'
					justifyContent={'space-between'}
					alignItems={'center'}
					sx={{ width: '100%' }}
				>
					<FormLabel
						sx={{
							mt: 2,
							fontSize: '0.875rem',
							fontWeight: 600,
							display: 'block',
						}}
					>
						{'Recent notes'}
					</FormLabel>
					{totalNotes > 0 && (
						<Link
							to={notesUrl}
							component={RouterLink}
							sx={{ textDecorationLine: 'none', paddingTop: '2px' }}
						>
							View all
						</Link>
					)}
				</Stack>
			</AccordionSummary>
			<AccordionDetails sx={{ padding: '0px' }}>
				<TransitionGroup>
					{isLoading || isError || notes.length === 0 ? (
						<Collapse in={true}>
							<Typography variant='task_updated' {...(isError && { color: 'error' })}>
								{isLoading
									? 'Loading notes...'
									: isError
										? 'Error loading notes'
										: 'No notes created yet'}
							</Typography>
						</Collapse>
					) : (
						notes.map((note) => (
							<RenderNote key={`note-${note.id}`} {...{ note, expanded }} />
						))
					)}
				</TransitionGroup>
			</AccordionDetails>
		</Accordion>
	);
};
CollapsibleNotesGroup.displayName = 'CollapsibleNotesGroup';
